<template>
  <el-select
    v-if="!hasOneBranch"
    ref="select"
    v-model="selectedBranchId"
    size="default"
    class="w-80 shrink-0"
    fit-input-width
    :loading="loading"
    :placeholder="$t('nav.main.items.location.select_branch.titlev2')"
    data-testid="uod-branch-select"
    @change="handleSelect"
  >
    <el-option
      v-for="branch in locationList"
      :key="branch.id"
      :label="optionLabel(branch)"
      :value="branch.id"
      :disabled="branch.disabled"
    />
  </el-select>
</template>

<script>
import th from '@tillhub/javascript-sdk'
import { useAuthStore } from '@shared/store/auth'
import { useBranchesStore } from '@shared/store/branches'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import {
  branchDataFactory,
  withAccess,
  getAutoBranchId
} from '../branch-context/branches'

export default {
  props: {
    modelValue: {
      type: Object,
      default: null
    }
  },
  emits: ['update:modelValue'],
  setup() {
    const authStore = useAuthStore()
    const { isAdmin, locations: userAssignedLocations } = storeToRefs(authStore)
    const isSupport = computed(() => !!authStore.isSupport)
    const { branchesCount } = storeToRefs(useBranchesStore())

    return {
      authStore,
      isAdmin,
      isSupport,
      userAssignedLocations,
      branchesCount
    }
  },
  data() {
    return {
      loading: false,
      branchesList: [],
      selectedBranchId: this.modelValue?.id ?? null
    }
  },
  computed: {
    hasOneBranch() {
      return this.branchesCount === 1
    },
    branchesData() {
      return this.branchesList.map(branchDataFactory)
    },
    userIsEverywhere() {
      return this.userAssignedLocations === null
    },
    locationList() {
      return withAccess(this.branchesData, this.userAssignedLocations)
    }
  },
  async created() {
    await this.authStore.waitThInitializated()
    await this.fetchBranches()
    this.autoSetBranch()
  },
  methods: {
    open() {
      this.$refs.select?.toggleMenu()
    },
    autoSetBranch() {
      // With a single branch the select is hidden, so pick it for every user,
      // admins and support included - otherwise the page has no branch at all.
      const accessibleBranches = this.locationList.filter((b) => !b.disabled)
      const isSelectionAccessible = accessibleBranches.some(
        (b) => b.id === this.selectedBranchId
      )
      if (
        this.branchesList.length &&
        this.selectedBranchId &&
        !isSelectionAccessible
      ) {
        this.selectedBranchId = null
        this.$emit('update:modelValue', null)
      }

      if (
        !this.selectedBranchId &&
        this.branchesData.length === 1 &&
        accessibleBranches.length === 1
      ) {
        return this.handleSelect(accessibleBranches[0].id)
      }

      const branchId = getAutoBranchId({
        isPrivileged: this.isAdmin || this.isSupport,
        hasSelection: !!this.selectedBranchId,
        branchesData: this.branchesData,
        locationList: this.locationList,
        userAssignedLocations: this.userAssignedLocations
      })
      if (branchId) this.handleSelect(branchId)
    },
    handleSelect(branchId) {
      // An assigned branch may be missing from the loaded list, never select it then
      const isAccessible = this.locationList.some(
        (b) => b.id === branchId && !b.disabled
      )
      if (!isAccessible) return
      this.selectedBranchId = branchId
      this.$emit('update:modelValue', this.getBranch(branchId))
    },
    getBranch(id) {
      const branch = this.branchesList.find((branch) => branch.id === id)
      return branchDataFactory(branch)
    },
    optionLabel(branch) {
      return branch.mms_unit_unzer_id
        ? `${branch.label} ${branch.mms_unit_unzer_id}`
        : branch.label
    },
    async fetchBranches() {
      this.loading = true
      const query = { limit: 100, deleted: false, extended: 'true' }
      if (!(this.isAdmin || this.isSupport || this.userIsEverywhere)) {
        query.active = true
      }
      try {
        await this.fetchAllPaginatedBranches(query)
      } catch (err) {
        this.$logException(err, {
          trackError: false,
          message: this.$t('common.error.action.read.multiple', {
            resources: this.$t('common.resource.branch.plural')
          })
        })
      } finally {
        this.loading = false
      }
    },
    async fetchAllPaginatedBranches(query) {
      const collected = []
      let batch = await th.branchesV1().getAll({ query })
      collected.push(...(batch.data || []))
      while (batch.next) {
        batch = await batch.next()
        collected.push(...(batch.data || []))
      }
      this.branchesList = collected
    }
  }
}
</script>
